import { useState, useRef } from 'react';
import { Button } from './button';
import { toast } from 'sonner';
import { Upload, X, Image, FileText, Loader2 } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function ImageUpload({ 
  onUpload, 
  multiple = false, 
  folder = '/uploads',
  maxFiles = 5,
  existingImages = [],
  label = 'Upload Images',
  accept = 'image/*,application/pdf'
}) {
  const [uploading, setUploading] = useState(false);
  const [images, setImages] = useState(existingImages);
  const fileInputRef = useRef(null);

  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    
    if (!multiple && files.length > 1) {
      toast.error('Only one file allowed');
      return;
    }
    
    if (images.length + files.length > maxFiles) {
      toast.error(`Maximum ${maxFiles} files allowed`);
      return;
    }
    
    setUploading(true);
    
    try {
      const uploadedFiles = [];
      
      for (const file of files) {
        if (file.size > 10 * 1024 * 1024) {
          toast.error(`${file.name} is too large (max 10MB)`);
          continue;
        }
        
        const formData = new FormData();
        formData.append('file', file);
        formData.append('folder', folder);
        
        const response = await axios.post(
          `${BACKEND_URL}/api/upload`,
          formData,
          { headers: { 'Content-Type': 'multipart/form-data' } }
        );
        
        uploadedFiles.push(response.data.url);
      }
      
      const newImages = [...images, ...uploadedFiles];
      setImages(newImages);
      onUpload(multiple ? newImages : newImages[0]);
      
      if (uploadedFiles.length > 0) {
        toast.success(`${uploadedFiles.length} file(s) uploaded`);
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Failed to upload file(s)');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const removeImage = (indexToRemove) => {
    const newImages = images.filter((_, index) => index !== indexToRemove);
    setImages(newImages);
    onUpload(multiple ? newImages : (newImages[0] || null));
  };

  const isPdf = (url) => url?.toLowerCase()?.endsWith('.pdf');

  return (
    <div className="space-y-3">
      <label className="text-sm font-medium text-gray-700">{label}</label>
      
      <div className="flex items-center gap-2">
        <input
          ref={fileInputRef}
          type="file"
          accept={accept}
          multiple={multiple}
          onChange={handleFileSelect}
          className="hidden"
          data-testid="file-upload-input"
        />
        <Button
          type="button"
          variant="outline"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading || (!multiple && images.length >= 1) || images.length >= maxFiles}
          className="flex items-center gap-2"
          data-testid="file-upload-button"
        >
          {uploading ? (
            <><Loader2 className="h-4 w-4 animate-spin" /> Uploading...</>
          ) : (
            <><Upload className="h-4 w-4" /> {label}</>
          )}
        </Button>
        {multiple && (
          <span className="text-xs text-gray-500">{images.length}/{maxFiles} files</span>
        )}
      </div>
      
      {images.length > 0 && (
        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2">
          {images.map((url, index) => (
            <div key={index} className="relative group aspect-square" data-testid={`uploaded-file-${index}`}>
              {isPdf(url) ? (
                <a href={url} target="_blank" rel="noopener noreferrer"
                  className="w-full h-full flex flex-col items-center justify-center rounded-lg border bg-gray-50 hover:bg-gray-100">
                  <FileText className="h-8 w-8 text-red-500 mb-1" />
                  <span className="text-[10px] text-gray-500">PDF</span>
                </a>
              ) : (
                <img
                  src={url}
                  alt={`Upload ${index + 1}`}
                  className="w-full h-full object-cover rounded-lg border"
                  onError={(e) => { e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="100" height="100"><rect fill="%23f3f4f6" width="100" height="100"/><text x="50%" y="50%" text-anchor="middle" fill="%239ca3af" dy=".3em" font-size="12">Error</text></svg>'; }}
                />
              )}
              <button
                type="button"
                onClick={() => removeImage(index)}
                className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity shadow-lg"
              >
                <X className="h-3 w-3" />
              </button>
            </div>
          ))}
        </div>
      )}
      
      {images.length === 0 && !uploading && (
        <div 
          onClick={() => fileInputRef.current?.click()}
          className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center cursor-pointer hover:border-teal-500 hover:bg-teal-50 transition-colors"
        >
          <Image className="h-8 w-8 mx-auto text-gray-400 mb-2" />
          <p className="text-sm text-gray-500">Click to upload or drag and drop</p>
          <p className="text-xs text-gray-400 mt-1">Images & PDFs up to 10MB</p>
        </div>
      )}
    </div>
  );
}
